'use client';

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Tag } from '@osmosis/tailwind-ui/components/Tag';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { bgPositiveDark } from '@osmosis/tailwind-ui/styles/background';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';

type Props = {
  // The watch is confirmed present in the database.
  confirmed: boolean;
  // Playback has stopped, so the status can be aria-announced without interrupting.
  shouldAnnounce: boolean;
};

/**
 * "Watched" pill for the Learn page header.
 */
const WatchedPill = ({ confirmed, shouldAnnounce }: Props) => {
  const intl = useIntl();

  if (!confirmed) return null;

  return (
    <>
      <Tag
        data-testid="learn-watched-pill"
        // Absolutely positioned so the DOM order is preserved for accessibility.
        className={cn(
          'absolute left-3 top-0 z-10 gap-1 !py-0.5 text-xs font-bold md:left-4',
          '!rounded-t-none !rounded-b-lg',
          bgPositiveDark,
          text0,
        )}
      >
        <Icon icon={faCircleCheck} size={12} aria-hidden />
        <FormattedMessage
          id="learn.header.watchedLabel"
          defaultMessage="Watched"
        />
      </Tag>
      {/*
        Announced once playback stops.
      */}
      <Box aria-live="polite" className="sr-only">
        {shouldAnnounce
          ? intl.formatMessage({
              id: 'learn.header.watchedAnnouncement',
              defaultMessage: 'Video marked as watched',
            })
          : ''}
      </Box>
    </>
  );
};

export default WatchedPill;
